import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiX, FiChevronDown } from 'react-icons/fi';
import './Autocomplete.scss';

/**
 * Componente de Autocomplete reutilizable
 */
const Autocomplete = ({
  value,
  onChange,
  onSearch,
  preloadedOptions = null,
  getOptionLabel = (option) => option.name || option.label || '',
  getOptionValue = (option) => option.id || option.value,
  placeholder = 'Buscar...',
  disabled = false,
  noResultsText = 'No se encontraron resultados',
  debounceTime = 300
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsTyping(false); // Ya no está escribiendo
        // Restaurar el valor seleccionado si se cierra sin seleccionar
        if (selectedOption) {
          setInputValue(getOptionLabel(selectedOption));
        } else if (!inputValue) {
          // Si no hay nada escrito ni seleccionado, limpiar
          setInputValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedOption, getOptionLabel, inputValue]);

  // Buscar opciones con debounce
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Solo buscar si el dropdown está abierto
    if (isOpen) {
      debounceTimerRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const results = await onSearch(inputValue);
          setOptions(results || []);
        } catch (error) {
          console.error('Error en búsqueda:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, debounceTime);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue, isOpen, onSearch, debounceTime]);

  // Resolver el nombre cuando el value o preloadedOptions cambian
  useEffect(() => {
    if (!value) {
      if (!isTyping) {
        setSelectedOption(null);
        setInputValue('');
      }
      return;
    }

    // Si ya tenemos el item correcto, no hacer nada
    if (selectedOption && String(getOptionValue(selectedOption)) === String(value)) {
      return;
    }

    // Buscar en preloadedOptions primero (más confiable, sin llamada async)
    if (preloadedOptions && preloadedOptions.length > 0) {
      const found = preloadedOptions.find(opt => String(getOptionValue(opt)) === String(value));
      if (found) {
        setSelectedOption(found);
        setInputValue(getOptionLabel(found));
        return;
      }
    }

    // Buscar en opciones ya cargadas internamente
    const alreadyLoaded = options.find(opt => String(getOptionValue(opt)) === String(value));
    if (alreadyLoaded) {
      setSelectedOption(alreadyLoaded);
      setInputValue(getOptionLabel(alreadyLoaded));
      return;
    }

    // Fallback: buscar vía onSearch
    onSearch('').then(results => {
      const items = results || [];
      setOptions(items);
      const found = items.find(opt => String(getOptionValue(opt)) === String(value));
      if (found) {
        setSelectedOption(found);
        setInputValue(getOptionLabel(found));
      }
    }).catch(() => {});
  }, [value, preloadedOptions]); // eslint-disable-line

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setIsTyping(true); // Marcar que está escribiendo

    // Si el usuario está escribiendo, limpiar la selección temporal
    if (selectedOption && newValue !== getOptionLabel(selectedOption)) {
      setSelectedOption(null);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setIsTyping(true); // Marcar que puede empezar a escribir
    // Seleccionar todo el texto para facilitar escribir nuevo término
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleSelect = (option) => {
    setIsTyping(false); // Ya no está escribiendo

    if (!option) {
      // Seleccionó "Todos"
      setSelectedOption(null);
      setInputValue('');
      setIsOpen(false);
      onChange('');
    } else {
      setSelectedOption(option);
      setInputValue(getOptionLabel(option));
      setIsOpen(false);
      onChange(getOptionValue(option));
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setIsTyping(false); // Ya no está escribiendo
    setSelectedOption(null);
    setInputValue('');
    setOptions([]);
    onChange('');

    // Enfocar el input después de limpiar
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={`autocomplete ${disabled ? 'autocomplete--disabled' : ''}`} ref={wrapperRef}>
      <div className="autocomplete__input-wrapper">
        <FiSearch className="autocomplete__icon autocomplete__icon--search" />

        <input
          ref={inputRef}
          type="text"
          className={`autocomplete__input ${selectedOption ? 'autocomplete__input--selected' : ''}`}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          autoComplete="off"
        />

        {(inputValue || selectedOption) && !disabled && (
          <button
            type="button"
            className="autocomplete__clear"
            onClick={handleClear}
            aria-label="Limpiar selección"
          >
            <FiX size={16} />
          </button>
        )}

        <FiChevronDown
          className={`autocomplete__icon autocomplete__icon--chevron ${isOpen ? 'autocomplete__icon--chevron-open' : ''}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className="autocomplete__dropdown">
          {loading ? (
            <div className="autocomplete__loading">
              <div className="loading-spinner-single" style={{ width: '20px', height: '20px' }}></div>
              <span>Buscando...</span>
            </div>
          ) : options.length > 0 ? (
            <ul className="autocomplete__options">
              {/* Opción "Todos" solo si no hay búsqueda activa */}
              {inputValue.length === 0 && (
                <li
                  className={`autocomplete__option autocomplete__option--all ${!selectedOption ? 'autocomplete__option--selected' : ''}`}
                  onClick={() => handleSelect(null)}
                >
                  Todos
                </li>
              )}

              {options.map((option, index) => {
                const optionValue = getOptionValue(option);
                const isSelected = selectedOption && String(getOptionValue(selectedOption)) === String(optionValue);

                return (
                  <li
                    key={optionValue || index}
                    className={`autocomplete__option ${isSelected ? 'autocomplete__option--selected' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    {getOptionLabel(option)}
                  </li>
                );
              })}
            </ul>
          ) : inputValue.length >= 2 ? (
            <div className="autocomplete__no-results">
              {noResultsText}
            </div>
          ) : (
            <div className="autocomplete__hint">
              Escribe al menos 3 caracteres para buscar
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
